// Amplitude (проект flat-glitter-354252, US): события, автотрекинг (просмотры с SPA-переходами,
// клики, формы, загрузки, ошибки сети, web vitals) и запись сессий (Session Replay, все сессии).
// Тот же ключ, что на лендинге: SDK ставит cookie на .lomora.ru, поэтому путь
// «лендинг → приложение» склеивается в одного пользователя.
//
// Базовый SDK + плагин записи, а не @amplitude/unified: его initAll всегда тянет ещё
// Experiment и Guides & Surveys (десяток чанков с CDN), которые не используются.
// SDK с записью тяжёлый, поэтому грузится отдельным чанком через import(), а не в основном
// бандле. Вызовы до загрузки копятся в очереди и уходят по порядку после init.
//
// Запись: уровень light (решение владельца 2026-10-16; до этого conservative прятал весь текст
// звёздочками и записи были бесполезны). Light сам маскирует пароли, email, телефоны и карты
// в полях ввода; остальной текст виден. Имена и email на экранах и в полях (приветствие,
// боковая панель, профиль, регистрация, автор модуля, админка) помечены amp-mask — политика
// обещает, что их в записи нет. Элементы с классом amp-block (корень /admin) не записываются
// вовсе. ym-hide-content рядом с amp-mask — для Вебвизора Метрики (сейчас выключен).
// Настройки Session Replay в кабинете Amplitude перекрывают код — после выката сверить запись.
//
// В dev (import.meta.env.PROD=false) не подключается — не сорим в прод.

import type { Types } from '@amplitude/analytics-browser';

type AmplitudeSdk = typeof import('@amplitude/analytics-browser');
type AmplitudeCall = (amplitude: AmplitudeSdk) => void;

// Ключ приёма событий публичен по своей природе (как ID счётчика Метрики).
export const AMPLITUDE_API_KEY = 'd57b0f27086b51677ecaa643a0cd75c6';

// Секреты в адресах: ?token= в ссылках из писем и ?ticket= потока уведомлений (адрес запроса
// попадает в сетевое событие при ответе 5xx).
const SECRET_PARAM = /^(?:token|ticket)=/i;

/** Убирает секретные параметры из адреса или строки запроса («a=1&ticket=…»). */
function stripSecrets(value: string): string {
  if (!/(?:token|ticket)=/i.test(value)) return value;
  const hashAt = value.indexOf('#');
  const hash = hashAt >= 0 ? value.slice(hashAt) : '';
  const rest = hashAt >= 0 ? value.slice(0, hashAt) : value;
  const queryAt = rest.indexOf('?');
  const query = queryAt >= 0 ? rest.slice(queryAt + 1) : rest;
  const kept = query.split('&').filter((part) => !SECRET_PARAM.test(part)).join('&');
  if (queryAt < 0) return kept + hash;
  const base = rest.slice(0, queryAt);
  return (kept ? `${base}?${kept}` : base) + hash;
}

function scrub(value: unknown): unknown {
  if (typeof value === 'string') return stripSecrets(value);
  if (Array.isArray(value)) return value.map(scrub);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, scrub(item)]));
  }
  return value;
}

/** Обогащение: вычищает секреты из свойств события (адрес страницы и предыдущей, запрос сети). */
const stripSecretsPlugin: Types.EnrichmentPlugin = {
  name: 'lomora-strip-secrets',
  type: 'enrichment',
  execute: async (event) => {
    if (event.event_properties) event.event_properties = scrub(event.event_properties) as Record<string, unknown>;
    if (event.user_properties) event.user_properties = scrub(event.user_properties) as Record<string, unknown>;
    return event;
  },
};

let sdk: AmplitudeSdk | null = null;
let loading = false;
let failed = false;
const queue: AmplitudeCall[] = [];

/** Однократная загрузка SDK и init (только в production-сборке). */
// То же, что autocapture: true, но с правилами сетевых событий. По умолчанию SDK пишет только
// ответы 5xx — отказ 402 при создании модуля (2026-10-15) в событиях не был
// виден. Для API пишем и 4xx, кроме 401 (обычное обновление токена), с кодом ошибки из тела
// ответа (`error.code`, без остальных полей); прочие хосты — по-прежнему только 5xx.
// Правила проверяются с конца массива.
const AUTOCAPTURE: Types.AutocaptureOptions = {
  attribution: true,
  fileDownloads: true,
  formInteractions: true,
  pageViews: true,
  sessions: true,
  elementInteractions: true,
  frustrationInteractions: true,
  webVitals: true,
  networkTracking: {
    captureRules: [
      { statusCodeRange: '500-599' },
      { hosts: ['api.lomora.ru'], statusCodeRange: '0,400,402-599', responseBody: { allowlist: ['error/code'] } },
    ],
  },
};

export function initAmplitude(): void {
  if (!import.meta.env.PROD || loading) return;
  loading = true;
  Promise.all([import('@amplitude/analytics-browser'), import('@amplitude/plugin-session-replay-browser')])
    .then(([amplitude, { sessionReplayPlugin }]) => {
      amplitude.add(
        sessionReplayPlugin({
          sampleRate: 1,
          privacyConfig: { defaultMaskLevel: 'light', blockSelector: '.amp-block' },
        }),
      );
      // Чистильщик — после init: плагины обогащения выполняются в порядке добавления, а адрес
      // страницы и предыдущей страницы дописывает встроенный плагин, который ставит init.
      amplitude
        .init(AMPLITUDE_API_KEY, { autocapture: AUTOCAPTURE })
        .promise.then(() => amplitude.add(stripSecretsPlugin))
        .catch(() => undefined);
      // Уход со страницы (закрыли вкладку, переход на оплату или в Яндекс ID): очередь уходит
      // через sendBeacon. Иначе события последней секунды ждут в localStorage следующего визита.
      window.addEventListener('pagehide', () => {
        amplitude.setTransport('beacon');
        amplitude.flush();
      });
      // Страница вернулась из bfcache — снова обычный fetch.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) amplitude.setTransport('fetch');
      });
      // Конец сессии на телефоне (владелец 2026-10-28: «чтобы амплитуда записывала корректно конец сессии»): вкладку
      // там не закрывают, а сворачивают браузер — уходят в почту или другое приложение, pagehide не приходит. Событие
      // app_hidden — последнее событие сессии: экран, на котором ушли, и сколько секунд он был открыт; очередь — beacon.
      let shownAt = Date.now();
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          amplitude.track('app_hidden', {
            path: window.location.pathname,
            visible_seconds: Math.round((Date.now() - shownAt) / 1000),
          });
          amplitude.setTransport('beacon');
          amplitude.flush();
        } else {
          shownAt = Date.now();
          amplitude.setTransport('fetch');
        }
      });
      sdk = amplitude;
      queue.splice(0).forEach((call) => call(amplitude));
    })
    .catch(() => {
      // Блокировщик или сеть: без аналитики приложение работает как обычно.
      failed = true;
      queue.length = 0;
    });
}

/** Выполнить вызов SDK сейчас или после загрузки. */
export function withAmplitude(call: AmplitudeCall): void {
  if (!import.meta.env.PROD || failed) return;
  if (sdk) call(sdk);
  else queue.push(call);
}
